import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import {
  Activity, BarChart3, Bot, CheckCircle2, ClipboardList,
  LayoutTemplate, PackageCheck, PhoneCall, RefreshCw, X,
  Plane, Receipt, Loader2
} from 'lucide-react';
import { useStores, invalidateStore, getAll, put, generateId } from '../hooks/useDB';
import { money, percent, isOverdue } from '../utils/formatters';
import { toast } from '../utils/toast';
import { confirm } from '../utils/confirm';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toneForPercent(value, warningAt = 65) {
  if (value >= 85) return 'good';
  if (value >= warningAt) return 'warn';
  return 'danger';
}

function topCategoryBars(expenses) {
  const totals = Object.entries(
    expenses.reduce((map, row) => {
      const cat = row.category || 'Misc';
      map[cat] = (map[cat] || 0) + (parseFloat(row.amount) || 0);
      return map;
    }, {})
  ).sort((a, b) => b[1] - a[1]);
  const total = totals.reduce((sum, [, v]) => sum + v, 0) || 1;
  return totals.slice(0, 5).map(([label, value]) => ({ label, value: percent(value, total) }));
}

function buildContacts(rawData, employeeView) {
  const contacts = [];
  rawData.events.forEach((ev) => {
    if (ev.primaryPocName || ev.primaryPocPhone || ev.primaryPocEmail) {
      contacts.push({
        source: 'Event', context: ev.name || 'Event',
        name: ev.primaryPocName || 'Event POC',
        phone: ev.primaryPocPhone, email: ev.primaryPocEmail
      });
    }
  });
  rawData.venues.forEach((v) => {
    if (v.contactName || v.contactPhone || v.contactEmail) {
      contacts.push({
        source: v.venueType || 'Venue', context: v.name || 'Venue',
        name: v.contactName || 'Venue POC',
        phone: v.contactPhone, email: v.contactEmail
      });
    }
  });
  rawData.schedule
    .filter((item) => !employeeView || item.published)
    .forEach((item) => {
      if (item.pocName || item.pocPhone) {
        contacts.push({
          source: item.type || 'Itinerary', context: item.title || 'Itinerary',
          name: item.pocName || 'Itinerary POC',
          phone: item.pocPhone, email: ''
        });
      }
    });
  return contacts;
}

// ---------------------------------------------------------------------------
// Admin surfaces builder
// ---------------------------------------------------------------------------

function buildAdminSurfaces(rawData, updatedAt) {
  const contacts = buildContacts(rawData, false);
  const expenses = rawData.budget.filter((r) => r.type === 'expense');
  const income   = rawData.budget.filter((r) => r.type === 'income');
  const totalSpent     = expenses.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const totalEstimated = expenses.reduce((s, r) => s + (parseFloat(r.estimated) || parseFloat(r.amount) || 0), 0);
  const totalIncome    = income.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const missingProofs  = expenses.filter((r) => !(r.attachments || []).length);
  const pendingExpenses = expenses.filter((r) => ['pending', 'approved', 'disputed'].includes(r.status));

  const confirmed     = rawData.attendees.filter((a) => a.rsvp === 'confirmed').length;
  const travelClosed  = rawData.attendees.filter((a) => ['booked', 'issued', 'completed'].includes(a.travelStatus)).length;
  const pendingTravel = rawData.attendees.filter((a) => (a.travelStatus || 'pending') === 'pending');
  const roomNeeded    = rawData.attendees.filter((a) => a.roomNeeded).length;
  const travelCost    = rawData.attendees.reduce((s, a) => s + (parseFloat(a.travelCost) || 0), 0);

  const tasksDone   = rawData.tasks.filter((t) => t.status === 'done').length;
  const overdueTasks  = rawData.tasks.filter(isOverdue);
  const blockedTasks  = rawData.tasks.filter((t) => t.status === 'blocked');

  const publishedItems = rawData.schedule.filter((s) => s.published);
  const agendaValidation = rawData.schedule.flatMap((item) => {
    const issues = [];
    if (!item.endTime && !item.openEnded) issues.push(`${item.title || 'Untitled'} is missing an end time`);
    if (!item.location && !item.conferenceRoom) issues.push(`${item.title || 'Untitled'} is missing a location`);
    if (!item.pocName && item.type !== 'break') issues.push(`${item.title || 'Untitled'} is missing a POC`);
    return issues;
  }).slice(0, 5);

  const inventoryNeeded = rawData.inventory.filter((i) => i.status === 'needed');
  const inventoryValue  = rawData.inventory.reduce((s, i) => s + ((parseFloat(i.unitPrice) || 0) * (parseInt(i.quantity, 10) || 0)), 0);
  const mealCost = rawData.meals.reduce((s, m) => {
    const heads = (parseInt(m.vegCount, 10) || 0) + (parseInt(m.nonVegCount, 10) || 0);
    return s + (parseFloat(m.pricePerHead) || 0) * heads;
  }, 0);

  const readinessScore = Math.round((
    percent(confirmed, Math.max(rawData.attendees.length, rawData.events[0]?.expectedAttendees || 0)) +
    percent(travelClosed, rawData.attendees.length || 1) +
    percent(tasksDone, rawData.tasks.length || 1) +
    percent(publishedItems.length, rawData.schedule.length || 1) +
    percent(expenses.length - missingProofs.length, expenses.length || 1)
  ) / 5);

  const pocCount = contacts.filter((c) => /event|venue/i.test(c.source)).length;

  return [
    // 1. Event Readiness
    {
      id: 'event-readiness',
      icon: Activity,
      label: 'Event Readiness',
      description: `${readinessScore}% readiness from RSVP, travel, tasks, agenda, and invoices. Last refresh ${updatedAt || 'just now'}.`,
      dataModel: {
        metrics: [
          { label: 'Readiness', value: `${readinessScore}%`, tone: toneForPercent(readinessScore) },
          { label: 'Confirmed RSVP', value: `${confirmed}/${rawData.attendees.length}`, tone: toneForPercent(percent(confirmed, rawData.attendees.length || 1)) },
          { label: 'Open Tasks', value: String(Math.max(rawData.tasks.length - tasksDone, 0)), tone: rawData.tasks.length === tasksDone ? 'good' : 'warn' },
          { label: 'POCs Ready', value: String(contacts.length), tone: contacts.length ? 'good' : 'danger' }
        ],
        checklist: [
          { text: 'Create at least one event plan', done: rawData.events.length > 0 },
          { text: 'Confirm attendee RSVP list', done: rawData.attendees.length > 0 && confirmed === rawData.attendees.length },
          { text: 'Close travel pending list', done: pendingTravel.length === 0 },
          { text: 'Resolve blocked or overdue tasks', done: blockedTasks.length === 0 && overdueTasks.length === 0 },
          { text: 'Attach proofs to budget expenses', done: missingProofs.length === 0 },
          { text: 'Publish full agenda', done: rawData.schedule.length > 0 && publishedItems.length === rawData.schedule.length }
        ],
        actions: ['Review risks', 'Open tasks', 'Refresh data']
      }
    },

    // 2. Finance Control
    {
      id: 'finance-control',
      icon: BarChart3,
      label: 'Finance',
      description: `${money(totalSpent)} spent against ${money(totalEstimated)} estimated. ${missingProofs.length} expense(s) need proof.`,
      dataModel: {
        metrics: [
          { label: 'Total Spent', value: money(totalSpent), tone: totalEstimated && totalSpent > totalEstimated ? 'danger' : 'good' },
          { label: 'Estimated', value: money(totalEstimated), tone: 'good' },
          { label: 'Income', value: money(totalIncome), tone: totalIncome ? 'good' : 'warn' },
          { label: 'Missing Proofs', value: String(missingProofs.length), tone: missingProofs.length ? 'danger' : 'good' }
        ],
        bars: topCategoryBars(expenses),
        table: {
          columns: ['Entry', 'Vendor/Owner', 'Status', 'Amount'],
          rows: (pendingExpenses.length ? pendingExpenses : expenses).slice(0, 6).map((r) => [
            r.title || 'Untitled',
            r.submittedByName || r.approver || r.vendor || 'Shared',
            r.status || 'pending',
            money(r.amount)
          ])
        },
        actions: ['Review approvals', 'Request proofs', 'Export CSV']
      }
    },

    // 3. Travel Roster
    {
      id: 'travel-roster',
      icon: ClipboardList,
      label: 'Travel',
      description: `${travelClosed}/${rawData.attendees.length} attendee travel records are booked, issued, or completed.`,
      dataModel: {
        notice: pendingTravel.length > 0 ? `${pendingTravel.length} traveler(s) still need booking closure.` : null,
        metrics: [
          { label: 'Travel Closed', value: `${travelClosed}/${rawData.attendees.length}`, tone: pendingTravel.length ? 'warn' : 'good' },
          { label: 'Rooms Needed', value: String(roomNeeded), tone: roomNeeded ? 'warn' : 'good' },
          { label: 'Pending Travel', value: String(pendingTravel.length), tone: pendingTravel.length ? 'danger' : 'good' },
          { label: 'Travel Cost', value: money(travelCost), tone: 'warn' }
        ],
        table: {
          columns: ['Attendee', 'Mode', 'Status', 'Ticket/Ref'],
          rows: (pendingTravel.length ? pendingTravel : rawData.attendees).slice(0, 7).map((a) => [
            a.name || 'Unnamed',
            a.travelMode || 'cab',
            a.travelStatus || 'pending',
            a.flightPnr || a.flightNo || a.cabBookingRef || '-'
          ])
        },
        actions: ['Copy pending list', 'Download roster']
      }
    },

    // 4. Agenda Builder
    {
      id: 'agenda-builder',
      icon: LayoutTemplate,
      label: 'Agenda',
      description: `${publishedItems.length}/${rawData.schedule.length} itinerary item(s) are published.`,
      dataModel: {
        metrics: [
          { label: 'Total Items', value: String(rawData.schedule.length), tone: rawData.schedule.length ? 'good' : 'warn' },
          { label: 'Published', value: `${publishedItems.length}/${rawData.schedule.length}`, tone: toneForPercent(percent(publishedItems.length, rawData.schedule.length || 1)) },
          { label: 'Validation Issues', value: String(agendaValidation.length), tone: agendaValidation.length ? 'danger' : 'good' },
          { label: 'POC Links', value: String(rawData.schedule.filter((s) => s.pocName || s.pocPhone).length), tone: 'good' }
        ],
        timeline: [...rawData.schedule]
          .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.startTime || '').localeCompare(b.startTime || ''))
          .slice(0, 8)
          .map((item) => ({
            time: item.startTime || '--:--',
            title: item.title || 'Untitled',
            meta: [
              item.day || item.date,
              item.location || item.conferenceRoom,
              !item.published ? '(unpublished)' : null
            ].filter(Boolean).join(' · ') || 'Unscheduled'
          })),
        validation: agendaValidation,
        actions: ['Publish agenda', 'Open itinerary']
      }
    },

    // 5. Contact Command
    {
      id: 'contact-command',
      icon: PhoneCall,
      label: 'Contacts',
      description: `${contacts.length} POC contact(s) compiled from events, venues, and itinerary.`,
      dataModel: {
        metrics: [
          { label: 'Total POCs', value: String(contacts.length), tone: contacts.length ? 'good' : 'danger' },
          { label: 'With Phone', value: String(contacts.filter((c) => c.phone).length), tone: 'good' },
          { label: 'With Email', value: String(contacts.filter((c) => c.email).length), tone: 'good' },
          { label: 'Event/Venue POCs', value: String(pocCount), tone: pocCount ? 'good' : 'warn' }
        ],
        contacts: contacts.slice(0, 8),
        actions: ['Open contacts page', 'Copy escalation list']
      }
    },

    // 6. Ops & Supplies
    {
      id: 'ops-supplies',
      icon: PackageCheck,
      label: 'Ops',
      description: `${rawData.inventory.length} inventory item(s), ${rawData.meals.length} meal plan(s), ${money(mealCost + inventoryValue)} ops value.`,
      dataModel: {
        metrics: [
          { label: 'Inventory', value: String(rawData.inventory.length), tone: rawData.inventory.length ? 'good' : 'warn' },
          { label: 'Items Needed', value: String(inventoryNeeded.length), tone: inventoryNeeded.length ? 'danger' : 'good' },
          { label: 'Meal Plans', value: String(rawData.meals.length), tone: rawData.meals.length ? 'good' : 'warn' },
          { label: 'Ops Value', value: money(mealCost + inventoryValue), tone: 'warn' }
        ],
        bars: Object.entries(
          rawData.inventory.reduce((map, i) => {
            const st = i.status || 'needed';
            map[st] = (map[st] || 0) + 1;
            return map;
          }, {})
        ).map(([label, value]) => ({ label, value })),
        table: {
          columns: ['Item', 'Category', 'Status', 'Owner'],
          rows: (inventoryNeeded.length ? inventoryNeeded : rawData.inventory).slice(0, 6).map((i) => [
            i.name || 'Unnamed item',
            i.category || 'Misc',
            i.status || 'needed',
            i.assignedTo || i.vendor || '-'
          ])
        },
        actions: ['Mark items ordered', 'Go to inventory']
      }
    }
  ];
}

// ---------------------------------------------------------------------------
// Employee surfaces builder
// ---------------------------------------------------------------------------

function buildEmployeeSurfaces(rawData, currentUser) {
  const myRecord = rawData.attendees.find(
    (a) => (a.email || '').toLowerCase() === (currentUser.email || '').toLowerCase()
  );

  const ev = rawData.events[0] || {};
  const venue = rawData.venues[0] || {};
  const publishedSchedule = rawData.schedule
    .filter((s) => s.published)
    .sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.startTime || '').localeCompare(b.startTime || ''));

  const myExpenses = rawData.budget.filter(
    (r) => (r.submittedByEmail || '').toLowerCase() === (currentUser.email || '').toLowerCase()
  );
  const myExpenseRows = myExpenses.filter((r) => r.type === 'expense');
  const totalSubmitted = myExpenseRows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const inReview  = myExpenseRows.filter((r) => ['pending', 'disputed'].includes(r.status)).length;
  const approved  = myExpenseRows.filter((r) => r.status === 'approved').length;
  const paidOut   = myExpenseRows.filter((r) => r.status === 'paid').length;

  const contacts = buildContacts(rawData, true);

  const myRsvp    = myRecord?.rsvp || 'pending';
  const myRsvpTone = myRsvp === 'confirmed' ? 'good' : myRsvp === 'declined' ? 'danger' : 'warn';
  const travelStatus = myRecord?.travelStatus || 'pending';
  const travelTone   = ['booked', 'issued', 'completed'].includes(travelStatus) ? 'good' : 'warn';

  const sessions    = publishedSchedule.filter((s) => s.type === 'session' || s.type === 'talk').length;
  const meals       = publishedSchedule.filter((s) => s.type === 'meal' || s.type === 'lunch' || s.type === 'dinner' || s.type === 'breakfast').length;
  const activities  = publishedSchedule.filter((s) => s.type === 'activity' || s.type === 'social').length;

  return [
    // 1. My Event
    {
      id: 'my-event',
      icon: Activity,
      label: 'My Event',
      description: ev.name ? `${ev.name} — check your RSVP and venue details.` : 'Your upcoming event overview.',
      dataModel: {
        metrics: [
          { label: 'My RSVP', value: myRsvp, tone: myRsvpTone },
          { label: 'Event Status', value: ev.status || 'planning', tone: ev.status === 'confirmed' ? 'good' : 'warn' },
          { label: 'T-Shirt Size', value: myRecord?.tShirtSize || 'not set', tone: myRecord?.tShirtSize ? 'good' : 'warn' },
          { label: 'Room Needed', value: myRecord?.roomNeeded ? 'Yes' : 'No', tone: myRecord?.roomNeeded ? 'warn' : 'good' }
        ],
        form: [
          { label: 'Venue', value: venue.name || ev.venue || 'TBD' },
          { label: 'Address', value: venue.address || 'TBD' },
          { label: 'Venue POC', value: venue.contactName || 'TBD' },
          { label: 'Venue Phone', value: venue.contactPhone || 'TBD' }
        ],
        actions: ['Update RSVP', 'Edit Profile', 'Open itinerary']
      }
    },

    // 2. My Itinerary
    {
      id: 'my-itinerary',
      icon: LayoutTemplate,
      label: 'Itinerary',
      description: `${publishedSchedule.length} published session(s) available.`,
      dataModel: {
        metrics: [
          { label: 'Sessions', value: String(sessions), tone: sessions ? 'good' : 'warn' },
          { label: 'Meals', value: String(meals), tone: meals ? 'good' : 'warn' },
          { label: 'Activities', value: String(activities), tone: activities ? 'good' : 'warn' },
          { label: 'Total Items', value: String(publishedSchedule.length), tone: publishedSchedule.length ? 'good' : 'warn' }
        ],
        timeline: publishedSchedule.slice(0, 10).map((item) => ({
          time: item.startTime || '--:--',
          title: item.title || 'Untitled',
          meta: [
            item.day || item.date,
            item.location || item.conferenceRoom,
            item.pocName ? `POC: ${item.pocName}` : null
          ].filter(Boolean).join(' · ') || 'Unscheduled'
        })),
        actions: ['Open full itinerary']
      }
    },

    // 3. My Travel
    {
      id: 'my-travel',
      icon: Plane,
      label: 'My Travel',
      description: myRecord ? `Travel status: ${travelStatus}` : 'No travel record found for your account.',
      dataModel: {
        notice: (myRecord && travelStatus === 'pending') ? 'Your travel booking is still pending. Contact your event admin.' : null,
        metrics: [
          { label: 'Travel Status', value: travelStatus, tone: travelTone },
          { label: 'Travel Mode', value: myRecord?.travelMode || 'N/A', tone: 'good' },
          { label: 'Departure', value: myRecord?.departureDate || 'TBD', tone: 'warn' },
          { label: 'Travel Cost', value: money(myRecord?.travelCost || 0), tone: 'warn' }
        ],
        form: myRecord ? [
          { label: 'Ticket / PNR', value: myRecord.flightPnr || myRecord.cabBookingRef || 'Not issued' },
          { label: 'Route', value: myRecord.fromCity && myRecord.toCity ? `${myRecord.fromCity} → ${myRecord.toCity}` : 'TBD' },
          { label: 'Return Date', value: myRecord.returnDate || 'TBD' },
          { label: 'Room Status', value: myRecord.roomNeeded ? (myRecord.roomBooked ? 'Booked' : 'Needed') : 'Not required' }
        ] : [],
        actions: myRecord ? ['Copy travel details', 'Flag an issue'] : []
      }
    },

    // 4. My Expenses
    {
      id: 'my-expenses',
      icon: Receipt,
      label: 'My Expenses',
      description: `${myExpenseRows.length} expense(s) submitted totaling ${money(totalSubmitted)}.`,
      dataModel: {
        metrics: [
          { label: 'Total Submitted', value: money(totalSubmitted), tone: totalSubmitted ? 'good' : 'warn' },
          { label: 'In Review', value: String(inReview), tone: inReview ? 'warn' : 'good' },
          { label: 'Approved', value: String(approved), tone: approved ? 'good' : 'warn' },
          { label: 'Paid Out', value: String(paidOut), tone: paidOut ? 'good' : 'warn' }
        ],
        table: {
          columns: ['Item', 'Category', 'Status', 'Amount'],
          rows: myExpenseRows.slice(0, 8).map((r) => [
            r.title || 'Untitled',
            r.category || 'Misc',
            r.status || 'pending',
            money(r.amount)
          ])
        },
        actions: ['Submit Expense', 'Go to invoices']
      }
    },

    // 5. My Contacts
    {
      id: 'my-contacts',
      icon: PhoneCall,
      label: 'Contacts',
      description: `${contacts.length} contact(s) from published itinerary, events, and venues.`,
      dataModel: {
        metrics: [
          { label: 'Total POCs', value: String(contacts.length), tone: contacts.length ? 'good' : 'danger' },
          { label: 'With Phone', value: String(contacts.filter((c) => c.phone).length), tone: 'good' },
          { label: 'Event POCs', value: String(contacts.filter((c) => c.source === 'Event').length), tone: 'good' },
          { label: 'Venue POCs', value: String(contacts.filter((c) => c.source !== 'Event' && c.source !== 'Itinerary').length), tone: 'good' }
        ],
        contacts: contacts,
        actions: ['Open contacts page']
      }
    }
  ];
}

// ---------------------------------------------------------------------------
// SurfaceCard component
// ---------------------------------------------------------------------------

function SurfaceCard({ surface, onAction, busyAction }) {
  const { id, icon: Icon, dataModel: dm } = surface;

  return (
    <div className="a2ui-surface">
      <div className="a2ui-surface-head">
        <div>
          <span className="a2ui-kicker">A2UI Live Board</span>
          <h4>{surface.label}</h4>
          <p>{surface.description}</p>
        </div>
        <Icon size={16} />
      </div>

      {dm.metrics && (
        <div className="a2ui-metric-grid">
          {dm.metrics.map((m) => (
            <div key={m.label} className={`a2ui-metric${m.tone ? ` ${m.tone}` : ''}`}>
              <span>{m.label}</span>
              <strong>{m.value}</strong>
            </div>
          ))}
        </div>
      )}

      {dm.notice && (
        <div className="a2ui-notice">{dm.notice}</div>
      )}

      {dm.bars && dm.bars.length > 0 && (
        <div className="a2ui-bars">
          {dm.bars.map((bar) => (
            <div key={bar.label} className="a2ui-bar-row">
              <span>{bar.label}</span>
              <div><i style={{ width: `${bar.value}%` }} /></div>
              <strong>{bar.value}%</strong>
            </div>
          ))}
        </div>
      )}

      {dm.table && dm.table.rows && dm.table.rows.length > 0 && (
        <div className="a2ui-table-wrap">
          <table className="a2ui-table">
            <thead>
              <tr>{dm.table.columns.map((col) => <th key={col}>{col}</th>)}</tr>
            </thead>
            <tbody>
              {dm.table.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dm.timeline && dm.timeline.length > 0 && (
        <div className="a2ui-timeline">
          {dm.timeline.map((item, i) => (
            <div key={i} className="a2ui-timeline-item">
              <span>{item.time}</span>
              <div>
                <strong>{item.title}</strong>
                <small>{item.meta}</small>
              </div>
            </div>
          ))}
        </div>
      )}

      {dm.checklist && dm.checklist.length > 0 && (
        <div className="a2ui-checklist">
          {dm.checklist.map((item, i) => (
            <div key={i}>
              <CheckCircle2 size={14} className={item.done ? 'done' : undefined} />
              <span>{item.text}</span>
            </div>
          ))}
        </div>
      )}

      {dm.contacts && dm.contacts.length > 0 && (
        <div className="a2ui-contact-list">
          {dm.contacts.map((c, i) => (
            <div key={i}>
              <strong>{c.name}</strong>
              <span>{c.context} · {c.source}</span>
              {c.phone && (
                <small><a href={`tel:${c.phone}`}>{c.phone}</a></small>
              )}
            </div>
          ))}
        </div>
      )}

      {dm.form && dm.form.length > 0 && (
        <div className="a2ui-form-preview">
          {dm.form.map((field) => (
            <label key={field.label}>
              <span>{field.label.toUpperCase()}</span>
              <input readOnly value={field.value} />
            </label>
          ))}
        </div>
      )}

      {dm.validation && dm.validation.length > 0 && (
        <div className="a2ui-validation">
          {dm.validation.map((issue, i) => (
            <span key={i}>{issue}</span>
          ))}
        </div>
      )}

      {dm.actions && dm.actions.length > 0 && (
        <div className="a2ui-action-row">
          {dm.actions.map((action) => {
            const key = `${id}:${action}`;
            const isBusy = busyAction === key;
            return (
              <button
                key={action}
                type="button"
                disabled={!!busyAction}
                onClick={() => onAction(id, action)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {isBusy && <Loader2 size={12} className="animate-spin" />}
                {action}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employee write form — inline within the panel
// ---------------------------------------------------------------------------

function RsvpForm({ currentRsvp, busy, onConfirm, onDecline, onCancel }) {
  return (
    <div className="a2ui-write-form">
      <div className="a2ui-write-form-title">Update Your RSVP</div>
      <p style={{ fontSize: 12, color: 'var(--text2)', margin: 0 }}>
        Current: <strong style={{ color: 'var(--text)' }}>{currentRsvp}</strong>
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-primary"
          style={{ flex: 1, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          {busy ? 'Saving…' : 'Confirm Attendance'}
        </button>
        <button
          className="btn btn-secondary"
          style={{ flex: 1, fontSize: 12, color: 'var(--danger)', borderColor: 'var(--danger)' }}
          disabled={busy}
          onClick={onDecline}
        >
          Decline
        </button>
      </div>
      <button className="btn btn-sm btn-secondary" disabled={busy} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function ExpenseForm({ draft, busy, onChange, onSubmit, onCancel }) {
  return (
    <div className="a2ui-write-form">
      <div className="a2ui-write-form-title">Submit Expense</div>
      <div className="a2ui-form-preview" style={{ gap: 8 }}>
        <label>
          <span>Description</span>
          <input
            value={draft.title}
            onChange={(e) => onChange({ ...draft, title: e.target.value })}
            placeholder="e.g. Hotel taxi, Team dinner"
          />
        </label>
        <label>
          <span>Amount (₹)</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={draft.amount}
            onChange={(e) => onChange({ ...draft, amount: e.target.value })}
            placeholder="0"
          />
        </label>
        <label>
          <span>Category</span>
          <select
            value={draft.category}
            onChange={(e) => onChange({ ...draft, category: e.target.value })}
            style={{
              background: 'var(--bg)', color: 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 4,
              padding: '4px 8px', fontSize: 12, minHeight: 32,
            }}
          >
            {['Travel', 'Meals', 'Accommodation', 'Office Supplies', 'Misc'].map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-primary"
          style={{ flex: 1, fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          disabled={busy}
          onClick={() => onSubmit(draft)}
        >
          {busy && <Loader2 size={12} className="animate-spin" />}
          {busy ? 'Submitting…' : 'Submit'}
        </button>
        <button className="btn btn-secondary" style={{ fontSize: 12 }} disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stores list
// ---------------------------------------------------------------------------

const STORES = ['events', 'venues', 'attendees', 'schedule', 'inventory', 'meals', 'budget', 'tasks'];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function AskPlanner({ open, onClose, currentUser }) {
  const [updatedAt, setUpdatedAt]       = useState('');
  const [activeSurfaceId, setActiveSurfaceId] = useState(null);
  const [busyAction, setBusyAction]     = useState(null);
  const [activeForm, setActiveForm]     = useState(null); // null | { type: 'rsvp'|'expense', draft: {} }

  const isAdmin = currentUser?.role !== 'employee';

  const storeData = useStores(STORES);
  const loading = storeData.loading;
  const rawData = useMemo(() => ({
    events:    storeData.events    || [],
    venues:    storeData.venues    || [],
    attendees: storeData.attendees || [],
    schedule:  storeData.schedule  || [],
    inventory: storeData.inventory || [],
    meals:     storeData.meals     || [],
    budget:    storeData.budget    || [],
    tasks:     storeData.tasks     || [],
  }), [storeData]);

  const totalRecords = useMemo(
    () => STORES.reduce((sum, store) => sum + (rawData[store]?.length || 0), 0),
    [rawData]
  );

  // Update timestamp whenever data finishes loading
  const prevLoadingRef = useRef(loading);
  useEffect(() => {
    if (prevLoadingRef.current && !loading) {
      setUpdatedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    }
    prevLoadingRef.current = loading;
  }, [loading]);

  // Build surfaces based on role
  const surfaces = useMemo(() => {
    if (isAdmin) return buildAdminSurfaces(rawData, updatedAt);
    return buildEmployeeSurfaces(rawData, currentUser || {});
  }, [rawData, updatedAt, isAdmin, currentUser]);

  const activeSurface = useMemo(
    () => surfaces.find((s) => s.id === activeSurfaceId) || surfaces[0] || null,
    [surfaces, activeSurfaceId]
  );

  // Escape key handler
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Helper: navigate to a page and close sidebar
  const navigate = useCallback((pageId) => {
    window.dispatchEvent(new CustomEvent('planner:navigate', { detail: pageId }));
    onClose();
  }, [onClose]);

  // Employee write: update RSVP
  const handleRsvpUpdate = useCallback(async (rsvp) => {
    setBusyAction('rsvp');
    try {
      const rawAttendees = await getAll('attendees');
      const me = rawAttendees.find(
        (a) => (a.email || '').toLowerCase() === (currentUser?.email || '').toLowerCase()
      );
      if (!me) { toast.error('No attendee record found for your account'); return; }
      await put('attendees', { ...me, rsvp });
      setActiveForm(null);
      toast.success(`RSVP updated to "${rsvp}"`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyAction(null);
    }
  }, [currentUser]);

  // Employee write: submit expense
  const handleExpenseSubmit = useCallback(async (draft) => {
    if (!draft.title.trim()) { toast.error('Description is required'); return; }
    if (!(parseFloat(draft.amount) > 0)) { toast.error('Enter a valid amount'); return; }
    setBusyAction('expense');
    try {
      const record = {
        id: generateId(),
        type: 'expense',
        title: draft.title.trim(),
        amount: parseFloat(draft.amount),
        category: draft.category || 'Misc',
        status: 'pending',
        // Mark as a reimbursement claim so it passes the backend's employee
        // budget rules and shows up in the employee's "My Invoices" list.
        reimbursement: true,
        paymentMethod: 'reimbursement',
        submittedByName: currentUser?.name || '',
        submittedByEmail: currentUser?.email || '',
        submittedAt: new Date().toISOString(),
      };
      await put('budget', record);
      setActiveForm(null);
      toast.success(`Expense "${record.title}" submitted for review`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyAction(null);
    }
  }, [currentUser]);

  // Bug fix #8: handleSurfaceAction — no chat, no answerQuestion
  const handleSurfaceAction = useCallback(async (surfaceId, action) => {
    const key = `${surfaceId}:${action}`;
    setBusyAction(key);
    try {
      // -----------------------------------------------------------------------
      // event-readiness
      // -----------------------------------------------------------------------
      if (surfaceId === 'event-readiness') {
        if (action === 'Review risks') {
          const [rawTasks, rawBudget, rawAttendees] = await Promise.all([
            getAll('tasks'), getAll('budget'), getAll('attendees')
          ]);
          // Bug fix #6: use imported isOverdue, NOT inline reimplementation
          const overdueCount   = rawTasks.filter(isOverdue).length;
          const missingProofs  = rawBudget.filter((e) => e.type === 'expense' && !(e.attachments || []).length).length;
          const pendingTravel  = rawAttendees.filter((a) => (a.travelStatus || 'pending') === 'pending').length;
          toast.info(`Risks: ${overdueCount} overdue · ${missingProofs} missing proof · ${pendingTravel} pending travel`);
          return;
        }
        if (action === 'Open tasks') { navigate('tasks'); return; }
        if (action === 'Refresh data') { invalidateStore(); toast.success('Data refreshed'); return; }
      }

      // -----------------------------------------------------------------------
      // finance-control
      // -----------------------------------------------------------------------
      if (surfaceId === 'finance-control') {
        if (action === 'Review approvals') {
          const rawBudget = await getAll('budget');
          // Bug fix #9: only approve status === 'pending', NOT disputed; show confirm first
          const pending = rawBudget.filter((r) => r.type === 'expense' && r.status === 'pending');
          if (!pending.length) { toast.success('No pending approvals'); return; }
          const ok = await confirm(`Approve ${pending.length} pending expense(s)?`, 'Approve Expenses');
          if (!ok) return;
          for (const item of pending) {
            await put('budget', { ...item, status: 'approved' });
          }
          window.dispatchEvent(new CustomEvent('planner:data-change'));
          toast.success(`${pending.length} expense(s) approved`);
          return;
        }
        if (action === 'Request proofs') {
          const rawBudget = await getAll('budget');
          const missing = rawBudget.filter((r) => r.type === 'expense' && !(r.attachments || []).length);
          if (!missing.length) { toast.success('All expenses have proofs'); return; }
          const list = missing.map((r) => `- ${r.title || 'Untitled'} (${r.submittedByName || r.vendor || 'Unknown'})`).join('\n');
          navigator.clipboard.writeText(list);
          toast.info(`${missing.length} need proof — list copied to clipboard`);
          return;
        }
        if (action === 'Export CSV') {
          const rawBudget = await getAll('budget');
          const header = ['Title', 'Category', 'Type', 'Status', 'Amount', 'Estimated', 'Vendor', 'SubmittedBy'].join(',');
          const csvRows = rawBudget.map((r) =>
            [r.title, r.category, r.type, r.status, r.amount, r.estimated, r.vendor, r.submittedByName]
              .map((f) => `"${String(f ?? '').replace(/"/g, '""')}"`)
              .join(',')
          );
          const csv = [header, ...csvRows].join('\n');
          // Bug fix #5: use Blob + URL.createObjectURL, NOT encodeURI
          const blob = new Blob([csv], { type: 'text/csv' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = 'finance.csv';
          document.body.appendChild(a); a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Finance CSV downloaded');
          return;
        }
      }

      // -----------------------------------------------------------------------
      // travel-roster
      // -----------------------------------------------------------------------
      if (surfaceId === 'travel-roster') {
        if (action === 'Copy pending list') {
          // Bug fix #3: clipboard only, no DB write, no fake PNR/flight data
          const rawAttendees = await getAll('attendees');
          const pending = rawAttendees.filter((a) => (a.travelStatus || 'pending') === 'pending');
          if (!pending.length) { toast.success('All travel closed'); return; }
          const list = pending.map((a) =>
            `${a.name || 'Unknown'} · ${a.travelMode || 'cab'} · ${a.fromCity || 'TBD'} → ${a.toCity || 'TBD'}`
          ).join('\n');
          navigator.clipboard.writeText(list);
          toast.success(`${pending.length} pending copied to clipboard`);
          return;
        }
        if (action === 'Download roster') {
          const rawAttendees = await getAll('attendees');
          const header = 'Name,Email,Role,RSVP,TravelMode,TravelStatus,From,To,Departure,Return,Cost,TicketRef';
          const csvRows = rawAttendees.map((a) =>
            [
              a.name, a.email, a.role, a.rsvp, a.travelMode, a.travelStatus,
              a.fromCity, a.toCity, a.departureDate, a.returnDate, a.travelCost,
              a.flightPnr || a.cabBookingRef || ''
            ]
              .map((f) => `"${String(f ?? '').replace(/"/g, '""')}"`)
              .join(',')
          );
          const csv = [header, ...csvRows].join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const url  = URL.createObjectURL(blob);
          const a    = document.createElement('a');
          a.href = url; a.download = 'travel_roster.csv';
          document.body.appendChild(a); a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success('Travel roster downloaded');
          return;
        }
      }

      // -----------------------------------------------------------------------
      // agenda-builder
      // -----------------------------------------------------------------------
      if (surfaceId === 'agenda-builder') {
        if (action === 'Publish agenda') {
          const rawSchedule = await getAll('schedule');
          const unpub = rawSchedule.filter((s) => !s.published);
          if (!unpub.length) { toast.success('All items already published'); return; }
          const ok = await confirm(`Publish ${unpub.length} item(s)?`, 'Publish Agenda');
          if (!ok) return;
          for (const item of unpub) {
            await put('schedule', { ...item, published: true });
          }
          window.dispatchEvent(new CustomEvent('planner:data-change'));
          toast.success(`${unpub.length} item(s) published`);
          return;
        }
        if (action === 'Open itinerary') { navigate('schedule'); return; }
        // Bug fix #4: "Fix validation" action removed — no hardcoded 'Sarah Jenkins' write
      }

      // -----------------------------------------------------------------------
      // contact-command
      // -----------------------------------------------------------------------
      if (surfaceId === 'contact-command') {
        if (action === 'Open contacts page') { navigate('contacts'); return; }
        if (action === 'Copy escalation list') {
          const rawVenues = await getAll('venues');
          const list = rawVenues.map((v) => `${v.name || 'Venue'}: ${v.contactName || 'TBD'} — ${v.contactPhone || 'No Phone'}`).join('\n');
          navigator.clipboard.writeText(list);
          toast.success('Escalation list copied to clipboard');
          return;
        }
      }

      // -----------------------------------------------------------------------
      // ops-supplies
      // -----------------------------------------------------------------------
      if (surfaceId === 'ops-supplies') {
        if (action === 'Mark items ordered') {
          const rawInv = await getAll('inventory');
          const needed = rawInv.filter((i) => i.status === 'needed' || !i.status);
          if (!needed.length) { toast.success('No items need ordering'); return; }
          const ok = await confirm(`Mark ${needed.length} item(s) as ordered?`, 'Order Items');
          if (!ok) return;
          for (const item of needed) {
            await put('inventory', { ...item, status: 'ordered' });
          }
          window.dispatchEvent(new CustomEvent('planner:data-change'));
          toast.success(`${needed.length} item(s) marked as ordered`);
          return;
        }
        if (action === 'Go to inventory') { navigate('inventory'); return; }
      }

      // -----------------------------------------------------------------------
      // my-event
      // -----------------------------------------------------------------------
      if (surfaceId === 'my-event') {
        if (action === 'Update RSVP') {
          const myRsvp = rawData.attendees.find(
            (a) => (a.email || '').toLowerCase() === (currentUser?.email || '').toLowerCase()
          )?.rsvp || 'pending';
          setActiveForm({ type: 'rsvp', draft: { currentRsvp: myRsvp } });
          return;
        }
        if (action === 'Edit Profile') {
          window.dispatchEvent(new CustomEvent('planner:open-profile'));
          onClose();
          return;
        }
        if (action === 'Open itinerary') { navigate('schedule'); return; }
        if (action === 'View contacts')  { navigate('contacts'); return; }
      }

      // -----------------------------------------------------------------------
      // my-itinerary
      // -----------------------------------------------------------------------
      if (surfaceId === 'my-itinerary') {
        if (action === 'Open full itinerary') { navigate('schedule'); return; }
      }

      // -----------------------------------------------------------------------
      // my-travel
      // -----------------------------------------------------------------------
      if (surfaceId === 'my-travel') {
        if (action === 'Copy travel details') {
          const rawAttendees = await getAll('attendees');
          const me = rawAttendees.find(
            (a) => (a.email || '').toLowerCase() === (currentUser?.email || '').toLowerCase()
          );
          if (!me) { toast.error('No travel record found'); return; }
          const text = [
            `Name: ${me.name || 'N/A'}`,
            `Mode: ${me.travelMode || 'N/A'}`,
            `Status: ${me.travelStatus || 'pending'}`,
            `Route: ${me.fromCity || 'TBD'} → ${me.toCity || 'TBD'}`,
            `Departure: ${me.departureDate || 'TBD'}`,
            `Return: ${me.returnDate || 'TBD'}`,
            `Ticket/PNR: ${me.flightPnr || me.cabBookingRef || 'Not issued'}`,
            `Cost: ${money(me.travelCost || 0)}`
          ].join('\n');
          navigator.clipboard.writeText(text);
          toast.success('Travel details copied to clipboard');
          return;
        }
        if (action === 'Flag an issue') {
          const rawAttendees = await getAll('attendees');
          const me = rawAttendees.find(
            (a) => (a.email || '').toLowerCase() === (currentUser?.email || '').toLowerCase()
          );
          if (!me) { toast.error('No travel record found'); return; }
          const text = [
            `Name: ${me.name || 'N/A'}`,
            `Email: ${me.email || 'N/A'}`,
            `Current Status: ${me.travelStatus || 'pending'}`,
            `Mode: ${me.travelMode || 'N/A'}`,
            `Route: ${me.fromCity || 'TBD'} → ${me.toCity || 'TBD'}`,
            'Please review and update my travel booking.'
          ].join('\n');
          navigator.clipboard.writeText(text);
          toast.info('Issue report copied — share with your event admin');
          return;
        }
      }

      // -----------------------------------------------------------------------
      // my-expenses
      // -----------------------------------------------------------------------
      if (surfaceId === 'my-expenses') {
        if (action === 'Submit Expense') {
          setActiveForm({ type: 'expense', draft: { title: '', amount: '', category: 'Travel' } });
          return;
        }
        if (action === 'Go to invoices') { navigate('invoices'); return; }
      }

      // -----------------------------------------------------------------------
      // my-contacts
      // -----------------------------------------------------------------------
      if (surfaceId === 'my-contacts') {
        if (action === 'Open contacts page') { navigate('contacts'); return; }
      }

    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyAction(null);
    }
  }, [navigate, currentUser, rawData, onClose]);

  return (
    <>
      <div className={`planner-agent-scrim ${open ? 'open' : ''}`} onClick={onClose} />
      <aside
        className={`planner-agent ${open ? 'open' : ''}`}
        aria-label="Ask Planner"
      >
        {/* Header */}
        <div className="planner-agent-header">
          <div className="planner-agent-avatar"><Bot size={18} /></div>
          <div>
            <div className="planner-agent-title">Ask Planner</div>
            <div className="planner-agent-subtitle">
              {loading ? 'Syncing…' : `${totalRecords} records · ${updatedAt || 'syncing'}`}
            </div>
          </div>
          <div className="planner-agent-close">
            <button className="btn btn-sm btn-secondary" onClick={onClose}>
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="planner-agent-actions">
          <span style={{
            fontSize: '10px', fontWeight: 700, padding: '2px 8px',
            borderRadius: '999px', letterSpacing: '0.05em',
            background: isAdmin ? 'rgba(251,146,60,0.15)' : 'rgba(34,211,238,0.15)',
            color: isAdmin ? '#c2410c' : '#0e7490',
            border: `1px solid ${isAdmin ? 'rgba(251,146,60,0.4)' : 'rgba(34,211,238,0.4)'}`
          }}>
            {isAdmin ? 'Admin' : 'Employee'}
          </span>
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => invalidateStore()}
            disabled={loading}
          >
            <RefreshCw
              size={12}
              style={loading ? { animation: 'spin 1s linear infinite' } : {}}
            />
            Sync
          </button>
        </div>

        {/* Surface selector */}
        <div className="planner-agent-prompts">
          {surfaces.map((s) => {
            const SurfaceIcon = s.icon;
            const isActive = s.id === (activeSurface?.id);
            return (
              <button
                key={s.id}
                className={`planner-prompt-btn ${isActive ? 'active' : ''}`}
                onClick={() => { setActiveSurfaceId(s.id); setActiveForm(null); }}
              >
                <SurfaceIcon size={12} /> {s.label}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="planner-agent-a2ui">
          {activeSurface && (
            <>
              <div className="a2ui-intro">
                <div>
                  <h3>{activeSurface.label}</h3>
                  <p>{activeSurface.description}</p>
                </div>
              </div>
              {activeForm?.type === 'rsvp' && (
                <RsvpForm
                  currentRsvp={activeForm.draft.currentRsvp}
                  busy={busyAction === 'rsvp'}
                  onConfirm={() => handleRsvpUpdate('confirmed')}
                  onDecline={() => handleRsvpUpdate('declined')}
                  onCancel={() => setActiveForm(null)}
                />
              )}

              {activeForm?.type === 'expense' && (
                <ExpenseForm
                  draft={activeForm.draft}
                  busy={busyAction === 'expense'}
                  onChange={(d) => setActiveForm((f) => ({ ...f, draft: d }))}
                  onSubmit={handleExpenseSubmit}
                  onCancel={() => setActiveForm(null)}
                />
              )}

              {!activeForm && (
                <SurfaceCard
                  surface={activeSurface}
                  onAction={handleSurfaceAction}
                  busyAction={busyAction}
                />
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
